import { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useTheme } from '../../../hooks/useTheme';

interface Option {
  id: string | number;
  name: string;
  label?: string;
  description?: string;
}

interface SelectFieldProps {
  name: string;
  label: string;
  options: Option[];
  value: string | number | undefined;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  isActive?: boolean;
  isNext?: boolean;
  disabled?: boolean;
}

export function SelectField({
  name,
  label,
  options,
  value,
  onChange,
  onFocus,
  onBlur,
  isActive = false,
  isNext = false,
  disabled = false
}: SelectFieldProps) {
  const theme = useTheme(state => state.getThemeStyles());
  const selectedOption = options.find(opt => opt.id === value);

  return (
    <Listbox
      value={value}
      onChange={(newValue) => onChange(String(newValue))}
      disabled={disabled}
    >
      <div className={theme.components.select.container.base}>
        <Listbox.Button
          className={`
            ${theme.components.select.field.select.base}
            ${isActive ? theme.components.select.field.select.active : ''}
            ${disabled ? theme.components.select.field.select.disabled : ''}
          `}
          onFocus={onFocus}
          onBlur={onBlur}
        >
          <span className={theme.components.select.field.wrapper}>
            {selectedOption ? (
              <span className={theme.components.select.field.select.text}>
                {selectedOption.label || selectedOption.name}
              </span>
            ) : (
              <span className={theme.components.select.field.select.placeholder}>
                Selecione uma opção
              </span>
            )}
          </span>
          <span className={theme.components.select.field.icon.wrapper}>
            <ChevronUpDownIcon
              className={theme.components.select.field.icon.svg}
              aria-hidden="true"
            />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className={theme.components.select.options.wrapper}>
            {options.map((option) => (
              <Listbox.Option
                key={option.id}
                value={option.id}
                className={({ active, selected }) => `
                  ${theme.components.select.options.item.base}
                  ${active ? theme.components.select.options.item.active : ''}
                  ${selected ? theme.components.select.options.item.selected : ''}
                `}
              >
                {({ selected, active }) => (
                  <div className={theme.components.select.options.item.content}>
                    <span className={
                      selected
                        ? theme.components.select.options.item.textSelected
                        : theme.components.select.options.item.textUnselected
                    }>
                      {option.label || option.name}
                    </span>
                    {selected && (
                      <span className={`
                        ${theme.components.select.options.checkbox.base}
                        ${selected ? theme.components.select.options.checkbox.selected : theme.components.select.options.checkbox.unselected}
                      `}>
                        <CheckIcon className={theme.components.select.options.checkbox.icon} aria-hidden="true" />
                      </span>
                    )}
                  </div>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
} 